import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube } from "react-icons/fa";
import SEO from '../SEO/SEO';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  HomeIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ReceiptPercentIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  CalculatorIcon,
  ShieldCheckIcon,
  KeyIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  BriefcaseIcon,
  UserGroupIcon,
  TruckIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  BeakerIcon,
  CubeIcon,
  HeartIcon,
  StarIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BookOpenIcon,
  GlobeAsiaAustraliaIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ShoppingBagIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BoltIcon,
  FireIcon,
  SunIcon,
  MoonIcon,
  CloudIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

// Available HeroIcons matching Home Page Product Section
export const HERO_ICON_OPTIONS = [
  { name: 'HomeIcon', icon: HomeIcon, label: 'Home' },
  { name: 'BuildingOffice2Icon', icon: BuildingOffice2Icon, label: 'Building' },
  { name: 'BuildingLibraryIcon', icon: BuildingLibraryIcon, label: 'Library' },
  { name: 'BanknotesIcon', icon: BanknotesIcon, label: 'Banknotes' },
  { name: 'CurrencyDollarIcon', icon: CurrencyDollarIcon, label: 'Dollar' },
  { name: 'CreditCardIcon', icon: CreditCardIcon, label: 'Credit Card' },
  { name: 'ReceiptPercentIcon', icon: ReceiptPercentIcon, label: 'Receipt' },
  { name: 'ChartBarIcon', icon: ChartBarIcon, label: 'Chart' },
  { name: 'PresentationChartLineIcon', icon: PresentationChartLineIcon, label: 'Presentation' },
  { name: 'ArrowTrendingUpIcon', icon: ArrowTrendingUpIcon, label: 'Trending Up' },
  { name: 'CalculatorIcon', icon: CalculatorIcon, label: 'Calculator' },
  { name: 'ShieldCheckIcon', icon: ShieldCheckIcon, label: 'Shield' },
  { name: 'KeyIcon', icon: KeyIcon, label: 'Key' },
  { name: 'DocumentTextIcon', icon: DocumentTextIcon, label: 'Document' },
  { name: 'ClipboardDocumentCheckIcon', icon: ClipboardDocumentCheckIcon, label: 'Clipboard' },
  { name: 'BriefcaseIcon', icon: BriefcaseIcon, label: 'Briefcase' },
  { name: 'UserGroupIcon', icon: UserGroupIcon, label: 'Users' },
  { name: 'TruckIcon', icon: TruckIcon, label: 'Truck' },
  { name: 'ScaleIcon', icon: ScaleIcon, label: 'Scale' },
  { name: 'WrenchScrewdriverIcon', icon: WrenchScrewdriverIcon, label: 'Wrench' },
  { name: 'PencilSquareIcon', icon: PencilSquareIcon, label: 'Edit' },
  { name: 'CheckCircleIcon', icon: CheckCircleIcon, label: 'Check' },
  { name: 'BeakerIcon', icon: BeakerIcon, label: 'Beaker' },
  { name: 'CubeIcon', icon: CubeIcon, label: 'Cube' },
  { name: 'HeartIcon', icon: HeartIcon, label: 'Heart' },
  { name: 'StarIcon', icon: StarIcon, label: 'Star' },
  { name: 'LightBulbIcon', icon: LightBulbIcon, label: 'Bulb' },
  { name: 'AcademicCapIcon', icon: AcademicCapIcon, label: 'Academic' },
  { name: 'BookOpenIcon', icon: BookOpenIcon, label: 'Book' },
  { name: 'GlobeAsiaAustraliaIcon', icon: GlobeAsiaAustraliaIcon, label: 'Globe' },
  { name: 'ChatBubbleLeftRightIcon', icon: ChatBubbleLeftRightIcon, label: 'Chat' },
  { name: 'PhoneIcon', icon: PhoneIcon, label: 'Phone' },
  { name: 'EnvelopeIcon', icon: EnvelopeIcon, label: 'Email' },
  { name: 'MapPinIcon', icon: MapPinIcon, label: 'Location' },
  { name: 'ShoppingBagIcon', icon: ShoppingBagIcon, label: 'Shopping' },
  { name: 'SparklesIcon', icon: SparklesIcon, label: 'Sparkles' },
  { name: 'RocketLaunchIcon', icon: RocketLaunchIcon, label: 'Rocket' },
  { name: 'BoltIcon', icon: BoltIcon, label: 'Bolt' },
  { name: 'FireIcon', icon: FireIcon, label: 'Fire' },
  { name: 'SunIcon', icon: SunIcon, label: 'Sun' },
  { name: 'MoonIcon', icon: MoonIcon, label: 'Moon' },
  { name: 'CloudIcon', icon: CloudIcon, label: 'Cloud' },
  { name: 'PlusIcon', icon: PlusIcon, label: 'Plus' }
];

export const HERO_ICON_MAP = HERO_ICON_OPTIONS.reduce((acc, opt) => {
  acc[opt.name] = opt.icon;
  return acc;
}, {});

export const normalizeHeroIconName = (name) => {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (HERO_ICON_MAP[trimmed]) return trimmed;

  const lower = trimmed.toLowerCase();
  const aliasMap = {
    location: 'MapPinIcon',
    mappin: 'MapPinIcon',
    home: 'HomeIcon',
    house: 'HomeIcon',
    building: 'BuildingOffice2Icon',
    office: 'BuildingOffice2Icon',
    library: 'BuildingLibraryIcon',
    bank: 'BuildingLibraryIcon',
    banknotes: 'BanknotesIcon',
    money: 'BanknotesIcon',
    cash: 'BanknotesIcon',
    dollar: 'CurrencyDollarIcon',
    card: 'CreditCardIcon',
    creditcard: 'CreditCardIcon',
    receipt: 'ReceiptPercentIcon',
    chart: 'ChartBarIcon',
    trending: 'ArrowTrendingUpIcon',
    calculator: 'CalculatorIcon',
    shield: 'ShieldCheckIcon',
    key: 'KeyIcon',
    document: 'DocumentTextIcon',
    clipboard: 'ClipboardDocumentCheckIcon',
    briefcase: 'BriefcaseIcon',
    users: 'UserGroupIcon',
    user: 'UserGroupIcon',
    truck: 'TruckIcon',
    scale: 'ScaleIcon',
    wrench: 'WrenchScrewdriverIcon',
    edit: 'PencilSquareIcon',
    pencil: 'PencilSquareIcon',
    check: 'CheckCircleIcon',
    checkcircle: 'CheckCircleIcon',
    beaker: 'BeakerIcon',
    cube: 'CubeIcon',
    heart: 'HeartIcon',
    star: 'StarIcon',
    bulb: 'LightBulbIcon',
    lightbulb: 'LightBulbIcon',
    academic: 'AcademicCapIcon',
    book: 'BookOpenIcon',
    globe: 'GlobeAsiaAustraliaIcon',
    chat: 'ChatBubbleLeftRightIcon',
    phone: 'PhoneIcon',
    email: 'EnvelopeIcon',
    envelope: 'EnvelopeIcon',
    shopping: 'ShoppingBagIcon',
    sparkles: 'SparklesIcon',
    rocket: 'RocketLaunchIcon',
    bolt: 'BoltIcon',
    fire: 'FireIcon',
    sun: 'SunIcon',
    moon: 'MoonIcon',
    cloud: 'CloudIcon',
    plus: 'PlusIcon'
  };

  if (aliasMap[lower]) return aliasMap[lower];

  const withIcon = `${trimmed}Icon`;
  if (HERO_ICON_MAP[withIcon]) return withIcon;

  return '';
};

export const resolveHeroIcon = (name, fallback = MapPinIcon) => {
  const normalized = normalizeHeroIconName(name);
  return (normalized && HERO_ICON_MAP[normalized]) ? HERO_ICON_MAP[normalized] : fallback;
};

// Normalizes and sanitizes HTML strings so internal/external anchor tags and line breaks render correctly
export const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;

  // Replace malformed or non-standard anchor tags (e.g. <a "link= /contact" > or <a link="/contact">)
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });

  // Convert newlines to <br /> if no HTML break/block tags are present
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }

  return html;
};

// YouTube Video Helpers
export const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const str = url.trim();
  const shortsMatch = str.match(/(?:youtube\.com\/(?:shorts\/|live\/))([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return shortsMatch[1];
  const watchMatch = str.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|v\/))([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];
  const shortMatch = str.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];
  const embedMatch = str.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];
  return null;
};

export const resolveVideoUrl = (video, defaultChannelUrl = 'https://www.youtube.com/@ahamhfc') => {
  if (!video) return defaultChannelUrl;
  if (video.video_url && typeof video.video_url === 'string' && video.video_url.trim()) {
    return video.video_url.trim();
  }
  if (video.url && typeof video.url === 'string' && video.url.trim()) {
    return video.url.trim();
  }
  if (video.link && typeof video.link === 'string' && video.link.trim()) {
    return video.link.trim();
  }
  // Check if img was mistakenly saved as YouTube URL
  if (typeof video.img === 'string' && (video.img.includes('youtube.com') || video.img.includes('youtu.be'))) {
    return video.img.trim();
  }
  return defaultChannelUrl || 'https://www.youtube.com/@ahamhfc';
};

export const resolveVideoThumbnail = (video) => {
  if (!video) return '';
  // Check if img is a valid direct image URL
  if (video.img && typeof video.img === 'string' && video.img.trim()) {
    const trimmed = video.img.trim();
    const isYouTubePage = trimmed.includes('youtube.com/watch') || trimmed.includes('youtube.com/shorts') || trimmed.includes('youtu.be/') || trimmed.includes('youtube.com/live');
    if (!isYouTubePage) {
      return trimmed;
    }
    // If img was a YouTube link, extract thumbnail from it
    const videoId = getYouTubeVideoId(trimmed);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  }

  // Fallback to video_url or link
  const targetUrl = video.video_url || video.link || video.url;
  if (targetUrl) {
    const videoId = getYouTubeVideoId(targetUrl);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  }

  return video.img || '';
};

export const extractMapEmbedUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const trimmed = rawUrl.trim();
  const iframeMatch = trimmed.match(/src=["']([^"']+)["']/i);
  if (iframeMatch) return iframeMatch[1];
  return trimmed;
};

const BranchPageTemplate = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    // Relative internal link routing
    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  };
  // Extract clean branch slug (e.g. "theni" from "/home-loan-in-theni", "/affordable-home-loan-in-theni", or ":slug")
  const rawPath = paramSlug || location.pathname;
  const slug = rawPath
    .replace(/^\/?(?:affordable-)?home-loan-in-/, '')
    .replace(/^\/|\/$/g, '') || 'madurai';
  const [branchData, setBranchData] = useState(null);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    city: '',
    loanRequirement: 'Home Construction Loan'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const getApiUrl = (endpoint) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocal ? 'http://localhost:8001' : '';
    return `${baseUrl}${endpoint}`;
  };

  useEffect(() => {
    if (branchData) {
      const defaultCity = sections?.hero?.form_city_default || branchData.branch_name || '';
      setFormData(prev => ({
        ...prev,
        city: defaultCity
      }));
      setOtp('');
      setOtpSent(false);
      setOtpVerified(false);
    }
  }, [slug, branchData?.id]);

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, mobile: newPhone }));
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
    }
  };

  const handleSendOtp = async () => {
    const cleanedMobile = formData.mobile.replace(/\D/g, '');
    if (!cleanedMobile || cleanedMobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSendingOtp(true);
    const loadingToast = toast.loading('Sending OTP...');

    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanedMobile }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success('OTP sent to your mobile number!', { id: loadingToast });
        if (data.dev_otp) {
          console.log('Dev OTP:', data.dev_otp);
        }
      } else {
        toast.error(data.message || 'Failed to send OTP. Please try again.', { id: loadingToast });
      }
    } catch (error) {
      console.error('OTP Send error:', error);
      toast.error('An error occurred while sending OTP. Please try again later.', { id: loadingToast });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    const verifyToast = toast.loading('Verifying OTP...');

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.mobile, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success('OTP verified successfully!', { id: verifyToast });
      } else {
        toast.error(data.message || 'Invalid or expired OTP', { id: verifyToast });
      }
    } catch (error) {
      console.error('OTP Verify error:', error);
      toast.error('An error occurred during verification.', { id: verifyToast });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    const cleanedMobile = formData.mobile.replace(/\D/g, '');
    if (cleanedMobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!otpVerified) {
      toast.error('Please verify your mobile number with OTP first');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting your enquiry...');

    try {
      const heroData = sections?.hero || {};
      const currentBranchName = branchData?.branch_name
        ? (branchData.branch_name.toLowerCase().includes('branch') ? branchData.branch_name : `${branchData.branch_name} Branch`)
        : (slug ? `${slug.charAt(0).toUpperCase() + slug.slice(1)} Branch` : 'Branch');

      const payload = {
        fullName: formData.fullName.trim(),
        mobile: cleanedMobile,
        city: formData.city.trim() || heroData.form_city_default || branchData?.branch_name || '',
        loanRequirement: formData.loanRequirement,
        branchName: currentBranchName,
        slug: slug,
        pageUrl: location.pathname
      };

      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocal ? 'http://localhost:8001/api/gmb-branches/submit-enquiry' : '/api/gmb-branches/submit-enquiry';

      const response = await axios.post(apiUrl, payload);

      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Enquiry submitted successfully!', { id: loadingToast });
        navigate('/thank-you');
      } else {
        toast.error(response.data?.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
      }
    } catch (err) {
      console.error('Enquiry submission error:', err);
      toast.error(err.response?.data?.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    const fetchBranchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/gmb-branches/page/${slug}`);
        if (response.data.success) {
          setBranchData(response.data.data);
          setSections(response.data.data.sections);
        }
      } catch (err) {
        console.error('Error fetching branch data:', err);
        setError('Branch page not found');
      } finally {
        setLoading(false);
      }
    };
    fetchBranchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#283079] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading branch page...</p>
        </div>
      </div>
    );
  }

  if (error || !sections) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h2>
          <p className="text-gray-500">{error || 'This branch page does not exist.'}</p>
          <Link to="/" className="mt-4 inline-block text-[#283079] font-bold hover:underline">Go Home</Link>
        </div>
      </div>
    );
  }

  const hero = sections.hero || {};
  const featuresStrip = sections.features_strip || {};
  const youtubeVideos = sections.youtube_videos || {};
  const whyChoose = sections.why_choose || {};
  const localInsights = sections.local_insights || {};
  const loanProducts = sections.loan_products || {};
  const howToApply = sections.how_to_apply || {};
  const eligibility = sections.eligibility || {};
  const customerStories = sections.customer_stories || {};
  const visitUs = sections.visit_us || {};
  const gallery = sections.gallery || {};
  const coverage = sections.coverage || {};
  const faq = sections.faq || {};
  const cta = sections.cta || {};
  const seo = sections.seo || {};

  const branchSchema = seo.schema || null;

  return (
    <div className="font-sans">
      <SEO
        title={seo.title || `Affordable Home Loan in ${branchData.branch_name}`}
        description={seo.description || ''}
        keywords={seo.keywords || ''}
        canonical={seo.canonical || ''}
        ogTitle={seo.og_title || seo.title || ''}
        ogDescription={seo.og_description || seo.description || ''}
        schema={branchSchema}
      />

      {/* Hero Section */}
      <div className="bg-[#283079]/80 pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">

            {/* Left Content Section */}
            <div className="text-white lg:w-[45%] space-y-6">
              {/* Breadcrumbs */}
              <div className="text-sm text-gray-300 flex space-x-2">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>›</span>
                <span className="text-gray-400">
                  {hero.breadcrumb_title || hero.breadcrumb_label || (branchData?.branch_name ? `Affordable Home Loan ${branchData.branch_name}` : 'Affordable Home Loan Madurai')}
                </span>
              </div>

              {/* Subheading */}
              <div className="flex items-center space-x-3 mt-4">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{hero.branch_label || `${branchData.branch_name} BRANCH`}</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                {hero.heading || 'Affordable Home'} <br />
                {hero.heading_line2 || 'Loan'} <span className="text-yellow-500">{hero.heading_highlight || `In ${branchData.branch_name}`}</span>
              </h1>

              {/* Description */}
              {hero.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-hero-description text-gray-300 text-base md:text-sm xl:text-base leading-relaxed max-w-xl [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(hero.description) }}
                />
              )}
              {hero.description2 && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-hero-description text-gray-300 text-base md:text-sm xl:text-base leading-relaxed max-w-xl [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(hero.description2) }}
                />
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:inline-flex w-full sm:w-max">
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  {hero.button1_text && (
                    hero.button1_link && hero.button1_link.startsWith('http') ? (
                      <a
                        href={hero.button1_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button1_text}
                      </a>
                    ) : hero.button1_link && hero.button1_link.startsWith('#') ? (
                      <button
                        type="button"
                        onClick={() => document.querySelector(hero.button1_link)?.scrollIntoView({ behavior: 'smooth' })}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                      >
                        {hero.button1_text}
                      </button>
                    ) : hero.button1_link ? (
                      <Link
                        to={hero.button1_link}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button1_text}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                      >
                        {hero.button1_text}
                      </button>
                    )
                  )}

                  {hero.button2_text && (
                    hero.button2_link && (hero.button2_link.startsWith('http') || hero.button2_link.startsWith('tel:') || hero.button2_link.startsWith('mailto:')) ? (
                      <a
                        href={hero.button2_link}
                        {...(hero.button2_link.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button2_text}
                      </a>
                    ) : hero.button2_link && hero.button2_link.startsWith('#') ? (
                      <button
                        type="button"
                        onClick={() => document.querySelector(hero.button2_link)?.scrollIntoView({ behavior: 'smooth' })}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                      >
                        {hero.button2_text}
                      </button>
                    ) : hero.button2_link ? (
                      <Link
                        to={hero.button2_link}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button2_text}
                      </Link>
                    ) : (
                      <a
                        href={`tel:${visitUs.phone || '+91 78239 11555'}`}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button2_text}
                      </a>
                    )
                  )}
                </div>

                {/* Reviews */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-start gap-4 sm:gap-6 pt-6 pb-2">
                  {hero.button3_text && (
                    hero.button3_link && hero.button3_link.startsWith('http') ? (
                      <a
                        href={hero.button3_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button3_text}
                      </a>
                    ) : hero.button3_link && hero.button3_link.startsWith('#') ? (
                      <button
                        type="button"
                        onClick={() => {
                          const target = document.querySelector(hero.button3_link) || document.getElementById('youtube-videos-section');
                          target?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                      >
                        {hero.button3_text}
                      </button>
                    ) : hero.button3_link ? (
                      <Link
                        to={hero.button3_link}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer inline-flex items-center justify-center text-center"
                      >
                        {hero.button3_text}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => document.getElementById('youtube-videos-section')?.scrollIntoView({ behavior: 'smooth' })}
                        style={{ backgroundColor: '#ffffff', color: '#283079' }}
                        className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                      >
                        {hero.button3_text}
                      </button>
                    )
                  )}
                  <div className="flex items-center gap-3 h-full pt-1">
                    <div className="flex flex-col">
                      <span className="text-white text-sm font-bold tracking-wide mb-1">Review & Rating</span>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center text-yellow-400">
                          {[1, 2, 3, 4, 5].map(i => (
                            <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                          ))}
                        </div>
                        <span className="text-white text-xs font-medium">({hero.review_rating || '4.5'}/5) ({hero.review_count || '5'} Reviews)</span>
                      </div>
                      <span className="text-white text-xs mt-1 font-medium tracking-wide">{hero.review_label || `${branchData.branch_name} Branch`}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Checkmarks */}
              {hero.features && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-y-6 pt-4 text-sm font-medium">
                  {hero.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center whitespace-nowrap">
                      <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Middle Form Section */}
            <div className="w-full lg:w-[30%] mt-10 lg:mt-0">
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-xl">
                <p className="text-xl lg:text-2xl font-bold text-[#283079] mb-2 font-serif">{hero.form_title || 'Quick Enquiry'}</p>
                <p className="text-gray-500 text-xs lg:text-sm mb-6">{hero.form_subtitle || `Get a callback from our ${branchData.branch_name} branch within 24 hours.`}</p>
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Full Name</label>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    />
                  </div>
                  {/* Mobile Number with Send OTP / Verified Button */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                      Mobile Number*
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        value={formData.mobile}
                        onChange={handlePhoneChange}
                        disabled={otpVerified || isSubmitting}
                        required
                        className={`flex-1 bg-gray-50 border text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors ${
                          otpVerified ? 'border-green-500 bg-green-50 text-green-900 font-medium' : 'border-gray-200'
                        }`}
                      />
                      {!otpVerified ? (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={isSendingOtp || formData.mobile.length !== 10}
                          className={`px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs transition-all whitespace-nowrap cursor-pointer border-0 ${
                            isSendingOtp || formData.mobile.length !== 10
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-[#283079] text-white hover:bg-[#1e245c]'
                          }`}
                        >
                          {isSendingOtp ? 'Sending...' : otpSent ? 'Resend OTP' : 'Send OTP'}
                        </button>
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-bold whitespace-nowrap">
                          <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
                          Verified
                        </div>
                      )}
                    </div>
                  </div>

                  {/* OTP Input Card - Visible when OTP has been sent and not yet verified */}
                  {otpSent && !otpVerified && (
                    <div className="bg-blue-50/80 p-3.5 rounded-xl border border-blue-200 animate-fadeIn space-y-2">
                      <label className="block text-xs font-bold text-blue-900">
                        Enter 6-Digit OTP Sent To Your Mobile
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="6-digit OTP"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="flex-1 bg-white border border-blue-300 text-gray-800 text-center text-base tracking-widest font-mono font-bold rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block p-2.5 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={isVerifyingOtp || otp.length !== 6}
                          className={`px-4 py-2 rounded-lg font-bold text-xs transition-all whitespace-nowrap border-0 cursor-pointer ${
                            isVerifyingOtp || otp.length !== 6
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                          }`}
                        >
                          {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                        </button>
                      </div>
                      <p className="text-[11px] text-blue-700 leading-tight">
                        Please enter the 6-digit code received via SMS.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">City / Area</label>
                    <input
                      type="text"
                      placeholder="Enter city or area"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Loan Requirement</label>
                    <select
                      value={formData.loanRequirement}
                      onChange={(e) => setFormData({ ...formData, loanRequirement: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    >
                      <option value="Home Construction Loan">Home Construction Loan</option>
                      <option value="Home Renovation Loan">Home Renovation Loan</option>
                      <option value="Home Purchase Loan">Home Purchase Loan</option>
                      <option value="Composite Loan">Composite Loan</option>
                      <option value="Home Extension Loan">Home Extension Loan</option>
                      <option value="Balance Transfer Loan">Balance Transfer Loan</option>
                      <option value="NRI Home Loan">NRI Home Loan</option>
                      <option value="Top-up Loan">Top-up Loan</option>
                      <option value="Mortgage Loan">Mortgage Loan</option>
                    </select>
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting || !otpVerified}
                      style={{
                        backgroundColor: !otpVerified ? '#9CA3AF' : '#DC2626',
                        color: '#eff1f6ff'
                      }}
                      className="w-full font-bold py-3 px-4 rounded-xl transition-colors duration-300 shadow-sm border-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : !otpVerified ? (
                        'Verify Mobile with OTP to Submit'
                      ) : (
                        'Submit Enquiry'
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 text-center mt-4 leading-tight">
                    By submitting, you agree to be contacted by Aham Housing Finance regarding your enquiry.
                  </p>
                </form>
              </div>

              {/* Social Media Links */}
              {hero.social_links && (
                <div className="flex justify-center space-x-4 mt-6">
                  {hero.social_links.facebook && <a href={hero.social_links.facebook} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm"><FaFacebookF /></a>}
                  {hero.social_links.instagram && <a href={hero.social_links.instagram} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm"><FaInstagram /></a>}
                  {hero.social_links.linkedin && <a href={hero.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm"><FaLinkedinIn /></a>}
                  {hero.social_links.youtube && <a href={hero.social_links.youtube} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm"><FaYoutube /></a>}
                </div>
              )}
            </div>

            {/* Right Images Section */}
            {hero.images && hero.images.length > 0 && (
              <div className="w-full lg:w-[30%] mt-10 lg:mt-0">
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {hero.images.map((img, idx) => (
                    <div key={idx} className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                      <img
                        src={img?.startsWith('http') || img?.startsWith('/') ? img : `/${img}`}
                        alt={`Branch ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Strip */}
      {featuresStrip.features && (
        <div className="bg-[#FFF8D6] py-5 border-b border-yellow-200">
          <div className="container mx-auto px-4 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center md:text-left text-[#283079] font-bold text-sm">
              {featuresStrip.features.map((f, idx) => (
                <div key={idx} className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                  {f.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* YouTube Videos Section */}
      {youtubeVideos.videos && (
        <div id="youtube-videos-section" className="bg-white py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
            <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-red-600"></div>
                <span className="text-red-600 font-semibold tracking-wider text-sm uppercase">{youtubeVideos.section_tag || 'LATEST VIDEOS'}</span>
                <div className="w-6 h-[2px] bg-red-600"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{youtubeVideos.heading}</h2>
              {youtubeVideos.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-youtube-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-red-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(youtubeVideos.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {youtubeVideos.videos.map((video, idx) => {
                const videoHref = resolveVideoUrl(video, youtubeVideos.youtube_channel_url);
                const thumbSrc = resolveVideoThumbnail(video);

                return (
                  <a
                    key={idx}
                    href={videoHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 block aspect-[9/16] cursor-pointer bg-gray-900"
                  >
                    <img
                      src={thumbSrc}
                      alt={video.title}
                      onError={(e) => {
                        const fallbackId = getYouTubeVideoId(video.video_url || video.link || video.url || video.img);
                        if (fallbackId && e.target.src !== `https://img.youtube.com/vi/${fallbackId}/hqdefault.jpg`) {
                          e.target.src = `https://img.youtube.com/vi/${fallbackId}/hqdefault.jpg`;
                        }
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70 group-hover:opacity-50"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 bg-red-600/90 rounded-full flex items-center justify-center text-white shadow-lg group-hover:bg-red-600 group-hover:scale-110 transition-all duration-300">
                        <FaYoutube className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                      <h3 className="text-white font-bold text-sm line-clamp-2">{video.title}</h3>
                    </div>
                  </a>
                );
              })}
            </div>
            <div className="text-center mt-10">
              <a href={youtubeVideos.youtube_channel_url || '#'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-red-600 font-bold hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100 py-3 px-6 rounded-full">
                <span>Visit Our YouTube Channel</span>
                <FaYoutube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Section */}
      {whyChoose.cards && (
        <div className="bg-[#F4F6F9] py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
            <div className="mb-12 max-w-3xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{whyChoose.section_tag || 'WHY AHAM'}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-6 font-serif">{whyChoose.heading}</h2>
              {whyChoose.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-whychoose-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(whyChoose.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whyChoose.cards.map((card, idx) => (
                <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                  <span className="text-yellow-500 font-bold text-lg block mb-4">{card.number}</span>
                  <p className="text-xl font-bold text-[#283079] mb-3 font-serif">{card.title}</p>
                  {card.description && (
                    <div
                      onClick={handleRichTextClick}
                      className="gmb-whychoose-description text-gray-500 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                      dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(card.description) }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Local Insights Section */}
      {localInsights.cards && (
        <div className="bg-white py-20 border-t border-gray-100">
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
            <div className="mb-12 max-w-3xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{localInsights.section_tag || 'LOCAL INSIGHTS'}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{localInsights.heading}</h2>
              {localInsights.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-localinsights-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(localInsights.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {localInsights.cards.map((card, idx) => {
                const IconComponent = resolveHeroIcon(card.icon, MapPinIcon);
                return (
                  <div key={idx} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
                    <div className={`w-12 h-12 ${idx % 2 === 0 ? 'bg-[#283079]/10' : 'bg-yellow-500/10'} rounded-full flex items-center justify-center mb-4`}>
                      <IconComponent className={`w-6 h-6 ${idx % 2 === 0 ? 'text-[#283079]' : 'text-yellow-600'}`} />
                    </div>
                    <h3 className="font-bold text-[#283079] mb-2">{card.title}</h3>
                    {card.description && (
                      <div
                        onClick={handleRichTextClick}
                        className="gmb-localinsights-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(card.description) }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Loan Products Section */}
      {loanProducts.products && (
        <div className="bg-white py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
            <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{loanProducts.section_tag || 'LOAN PRODUCTS'}</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{loanProducts.heading}</h2>
              {loanProducts.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-loanproducts-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(loanProducts.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {loanProducts.products.map((product, idx) => {
                const ProductIcon = resolveHeroIcon(product.icon, BuildingOffice2Icon);
                return (
                  <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
                    <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                      <ProductIcon className="w-5 h-5 text-[#283079]" />
                    </div>
                  <h3 className="text-lg font-bold text-[#283079] mb-3">{product.title}</h3>
                  {product.description && (
                    <div
                      onClick={handleRichTextClick}
                      className="gmb-loanproducts-description text-gray-500 text-sm leading-relaxed mb-6 flex-grow [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                      dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(product.description) }}
                    />
                  )}
                  <Link to={product.link || '#'} className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                    {product.button_text || product.button_name || 'Learn More'} <span className="ml-1">→</span>
                  </Link>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* The Process Section */}
      {howToApply.steps && (
        <div className="bg-[#F4F6F9] py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{howToApply.section_tag || 'THE PROCESS'}</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{howToApply.heading}</h2>
              {howToApply.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-howtoapply-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(howToApply.description) }}
                />
              )}
            </div>
            <div className="relative max-w-6xl mx-auto">
              <div className="hidden md:block absolute top-6 left-[8%] right-[8%] h-[2px] border-t-2 border-dashed border-gray-300 z-0"></div>
              <div className={`grid grid-cols-1 md:grid-cols-${howToApply.steps.length} gap-6 md:gap-4 text-center relative z-10`}>
                {howToApply.steps.map((step, idx) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">{step.number}</div>
                    <p className="font-bold text-[#283079] text-sm mb-2">{step.title}</p>
                    {step.description && (
                      <div
                        onClick={handleRichTextClick}
                        className="gmb-howtoapply-description text-gray-500 text-xs leading-relaxed max-w-[150px] [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(step.description) }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Eligibility Section */}
      {eligibility.cards && (
        <div className="bg-white py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-5xl">
            <div className="text-center mb-12 max-w-2xl mx-auto flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{eligibility.section_tag || 'ELIGIBILITY'}</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{eligibility.heading}</h2>
              {eligibility.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-eligibility-description text-gray-600 text-base md:text-lg leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(eligibility.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eligibility.cards.map((card, idx) => (
                <div key={idx} className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
                  <div className="text-yellow-600 flex-shrink-0 mt-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm mb-1">{card.title}</h3>
                    {card.description && (
                      <div
                        onClick={handleRichTextClick}
                        className="text-gray-500 text-xs leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(card.description) }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Customer Stories Section */}
      {customerStories.reviews && (
        <div className="bg-[#F4F6F9] py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-6xl">
            <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{customerStories.section_tag || 'CUSTOMER STORIES'}</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{customerStories.heading}</h2>
              {customerStories.description && (
                <div
                  onClick={handleRichTextClick}
                  className="gmb-customerstories-description text-gray-600 text-sm md:text-base leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                  dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(customerStories.description) }}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {customerStories.reviews.map((review, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
                  <div className="flex text-yellow-500 mb-4 text-sm space-x-1">
                    {[1, 2, 3, 4, 5].map(i => (
                      <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                    ))}
                  </div>
                  {review.text && (
                    <div
                      onClick={handleRichTextClick}
                      className="gmb-customerstories-description text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                      dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(review.text) }}
                    />
                  )}
                  <div className="flex items-center space-x-3 mt-auto">
                    <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#283079] font-bold text-xs flex items-center justify-center flex-shrink-0">{review.initials}</div>
                    <div className="flex flex-col">
                      <p className="font-bold text-[#283079] text-xs">{review.name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Visit Us Section */}
      {visitUs.address && (
        <div className="bg-white py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="lg:w-1/2">
                <div className="mb-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-6 h-[2px] bg-yellow-500"></div>
                    <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{visitUs.section_tag || 'VISIT US'}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{visitUs.heading}</h2>
                  {visitUs.description && (
                    <div
                      onClick={handleRichTextClick}
                      className="gmb-visitus-description text-gray-600 text-sm md:text-base leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                      dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(visitUs.description) }}
                    />
                  )}
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
                  <h3 className="text-xl font-bold text-[#283079] mb-6 font-serif">{visitUs.branch_info_title}</h3>
                  <div className="space-y-4">
                    <div className="flex items-start pb-4 border-b border-gray-100">
                      <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">ADDRESS</p>
                        <p className="text-sm font-bold text-gray-800">{visitUs.address}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{visitUs.city_state_pin}</p>
                      </div>
                    </div>
                    <div className="flex items-start pb-4 border-b border-gray-100">
                      <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">PHONE</p>
                        <p className="text-sm font-bold text-gray-800">{visitUs.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start pb-4 border-b border-gray-100">
                      <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">EMAIL</p>
                        <p className="text-sm font-bold text-gray-800">{visitUs.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start pb-4 border-b border-gray-100">
                      <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">WORKING HOURS</p>
                        <p className="text-sm font-bold text-gray-800">{visitUs.working_hours}</p>
                      </div>
                    </div>
                    {visitUs.landmark && (
                      <div className="flex items-start pb-6">
                        <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">LANDMARK</p>
                          <p className="text-sm font-bold text-gray-800">{visitUs.landmark}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {/* Button 1 (Call Branch) */}
                    {(visitUs.button1_text !== '' && (visitUs.button1_text || 'Call Branch')) && (() => {
                      const text = visitUs.button1_text || 'Call Branch';
                      const link = visitUs.button1_link ? visitUs.button1_link.trim() : '';

                      if (!link) {
                        return (
                          <button
                            type="button"
                            className="border border-[#283079] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-50 transition-colors duration-300"
                          >
                            {text}
                          </button>
                        );
                      }

                      const isExternal = link.startsWith('http://') || link.startsWith('https://');
                      const isTelOrMail = link.startsWith('tel:') || link.startsWith('mailto:');
                      const isAnchor = link.startsWith('#');

                      if (isExternal || isTelOrMail) {
                        return (
                          <a
                            href={link}
                            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            className="border border-[#283079] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-50 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                          >
                            {text}
                          </a>
                        );
                      }
                      if (isAnchor) {
                        return (
                          <button
                            type="button"
                            onClick={() => document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' })}
                            className="border border-[#283079] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-50 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                          >
                            {text}
                          </button>
                        );
                      }
                      return (
                        <Link
                          to={link}
                          className="border border-[#283079] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-50 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                        >
                          {text}
                        </Link>
                      );
                    })()}

                    {/* Button 2 (Get Directions) */}
                    {(visitUs.button2_text !== '' && (visitUs.button2_text || 'Get Directions')) && (() => {
                      const text = visitUs.button2_text || 'Get Directions';
                      const link = visitUs.button2_link ? visitUs.button2_link.trim() : '';

                      if (!link) {
                        return (
                          <button
                            type="button"
                            className="bg-[#EEF2FF] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-100 transition-colors duration-300"
                          >
                            {text}
                          </button>
                        );
                      }

                      const isExternal = link.startsWith('http://') || link.startsWith('https://');
                      const isTelOrMail = link.startsWith('tel:') || link.startsWith('mailto:');
                      const isAnchor = link.startsWith('#');

                      if (isExternal || isTelOrMail) {
                        return (
                          <a
                            href={link}
                            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            className="bg-[#EEF2FF] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-100 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                          >
                            {text}
                          </a>
                        );
                      }
                      if (isAnchor) {
                        return (
                          <button
                            type="button"
                            onClick={() => document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' })}
                            className="bg-[#EEF2FF] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-100 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                          >
                            {text}
                          </button>
                        );
                      }
                      return (
                        <Link
                          to={link}
                          className="bg-[#EEF2FF] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-100 transition-colors duration-300 inline-flex items-center justify-center text-center cursor-pointer"
                        >
                          {text}
                        </Link>
                      );
                    })()}
                  </div>
                </div>
              </div>
              {visitUs.map_embed_url && (
                <div className="lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden w-full h-[300px] lg:h-full min-h-[400px] shadow-sm border border-gray-100">
                    <iframe title={`${branchData.branch_name} Branch Map`} width="100%" height="100%" style={{ border: 0 }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={extractMapEmbedUrl(visitUs.map_embed_url)}></iframe>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Section */}
      {gallery.images && gallery.images.length > 0 && (
        <div className="bg-white py-16 overflow-hidden border-t border-gray-100">
          <style>{`
            @keyframes infinite-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            .animate-infinite-scroll { display: flex; width: max-content; animation: infinite-scroll 30s linear infinite; }
            .animate-infinite-scroll:hover { animation-play-state: paused; }
          `}</style>
          <div className="container mx-auto px-4 lg:px-12 max-w-7xl mb-10">
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{gallery.section_tag || 'GALLERY'}</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] text-center font-serif">{gallery.heading || 'Life At Our Branch'}</h2>
          </div>
          <div className="relative w-full">
            <div className="animate-infinite-scroll space-x-4 md:space-x-6 px-4">
              {[...gallery.images, ...gallery.images].map((img, idx) => (
                <div key={idx} className="w-[260px] md:w-[320px] h-[200px] md:h-[240px] flex-shrink-0 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-transform hover:-translate-y-1">
                  <img
                    src={img?.startsWith('http') || img?.startsWith('/') ? img : `/${img}`}
                    alt={`Gallery item ${idx}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Coverage Section */}
      {coverage.areas && (
        <div className="bg-[#F4F6F9] py-16">
          <div className="container mx-auto px-4 lg:px-12 max-w-5xl text-center">
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{coverage.section_tag || 'COVERAGE'}</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-8 font-serif">{coverage.heading}</h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {coverage.areas.map((area, idx) => (
                <span key={idx} className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">{area}</span>
              ))}
            </div>
            {coverage.extra_label && (
              <div className="mt-5 flex justify-center">
                <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">{coverage.extra_label}</span>
              </div>
            )}
            {coverage.nearby_branches && coverage.nearby_branches.length > 0 && (
              <div className="mt-16 border-t border-gray-200 pt-10">
                <h3 className="text-2xl md:text-3xl font-bold text-[#283079] mb-8 font-serif">{coverage.nearby_branches_heading}</h3>
                <div className="flex flex-wrap justify-center gap-5 max-w-3xl mx-auto">
                  {coverage.nearby_branches.map((branch, idx) => (
                    <Link key={idx} to={branch.link || '#'} className="bg-white border-2 border-[#283079] text-[#283079] hover:bg-[#283079] hover:text-white font-bold text-base md:text-lg py-3 px-10 rounded-full transition-colors duration-300">
                      {branch.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {faq.faqs && (
        <div className="bg-white py-20">
          <div className="container mx-auto px-4 lg:px-12 max-w-4xl">
            <div className="text-center mb-12 flex flex-col items-center">
              <div className="flex items-center space-x-3 mb-4 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">{faq.section_tag || 'FAQ'}</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">{faq.heading}</h2>
            </div>
            <div className="space-y-4">
              {faq.faqs.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm cursor-pointer transition-all duration-200 hover:border-gray-300" onClick={() => toggleFaq(index)}>
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-[#283079] text-sm md:text-base">{item.question}</h3>
                    <svg className={`w-4 h-4 text-yellow-500 flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === index ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                    {item.answer && (
                      <div
                        onClick={handleRichTextClick}
                        className="text-gray-500 text-xs md:text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold [&_a]:transition-colors"
                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(item.answer) }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-[#F4F6F9] py-16 pb-24">
        <div className="container mx-auto px-4 lg:px-12 max-w-6xl">
          <div className="bg-[#283079] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden shadow-lg">
            {/* Subtle left glow overlay */}
            <div className="absolute top-0 left-0 w-64 h-full bg-white opacity-[0.03] mix-blend-overlay blur-3xl transform -translate-x-1/2"></div>

            <div className="relative z-10 flex flex-col items-center">
              {/* Caption / Subheading */}
              <div className="flex items-center space-x-3 mb-6 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-xs md:text-sm uppercase">
                  {cta.section_tag || 'GET STARTED'}
                </span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>

              {/* Heading */}
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-6 font-serif">
                {cta.heading || 'Ready To Explore Your Home Loan Options?'}
              </h2>

              {/* Paragraph */}
              <div
                onClick={handleRichTextClick}
                className="gmb-cta-description text-gray-300 text-xs md:text-sm leading-relaxed max-w-2xl mx-auto mb-10 [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndFormatHtml(
                    cta.description || `Speak with our ${branchData.branch_name} team to understand eligibility, documents required, and available loan options.`
                  ),
                }}
              />

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 w-full sm:w-auto">
                {/* Button 1 */}
                {cta.button1_link && cta.button1_link.startsWith('http') ? (
                  <a
                    href={cta.button1_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button1_text || 'Check Eligibility Now'}
                  </a>
                ) : cta.button1_link && !cta.button1_link.startsWith('#') ? (
                  <Link
                    to={cta.button1_link}
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button1_text || 'Check Eligibility Now'}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cta.button1_text || 'Check Eligibility Now'}
                  </button>
                )}

                {/* Button 2 */}
                {cta.button2_link && cta.button2_link.startsWith('http') ? (
                  <a
                    href={cta.button2_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button2_text || 'Request A Callback'}
                  </a>
                ) : cta.button2_link && !cta.button2_link.startsWith('#') ? (
                  <Link
                    to={cta.button2_link}
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button2_text || 'Request A Callback'}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cta.button2_text || 'Request A Callback'}
                  </button>
                )}

                {/* Button 3 */}
                {cta.button3_link && cta.button3_link.startsWith('http') ? (
                  <a
                    href={cta.button3_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button3_text || 'WhatsApp Our Team'}
                  </a>
                ) : cta.button3_link ? (
                  <a
                    href={cta.button3_link}
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button3_text || 'WhatsApp Our Team'}
                  </a>
                ) : (
                  <a
                    href={`https://wa.me/917823911555?text=Hi%2C%20I%20am%20interested%20in%20a%20home%20loan%20in%20${encodeURIComponent(branchData.branch_name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                  >
                    {cta.button3_text || 'WhatsApp Our Team'}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchPageTemplate;
